const API_URL = process.env.REACT_APP_API_URL;
const SESSION_KEY = "trustbud:web-session";
const CSRF_COOKIE_NAME = "trustbud_csrf";
let refreshPromise = null;
let logoutRequestInFlight = false;
let refreshBlockedUntil = 0;

function getCookie(name) {
  if (typeof document === "undefined") return "";
  return document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${name}=`))
    ?.slice(name.length + 1) || "";
}

export function csrfHeaders() {
  const token = getCookie(CSRF_COOKIE_NAME);
  return token ? { "X-CSRFToken": decodeURIComponent(token) } : {};
}

function markSessionActive(tokens = {}) {
  localStorage.setItem(SESSION_KEY, "true");
  if (tokens?.access) {
    localStorage.setItem("access", tokens.access);
  }
  if (tokens?.refresh) {
    localStorage.setItem("refresh", tokens.refresh);
  }
  refreshBlockedUntil = 0;
}

function replaceStoredAuthSession(tokens = {}) {
  localStorage.removeItem("access");
  localStorage.removeItem("refresh");
  markSessionActive(tokens);
}

function clearStoredAuthSession() {
  const hadSession = hasAuthSession();
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem("access");
  localStorage.removeItem("refresh");
  refreshBlockedUntil = 0;
  return hadSession;
}

function refreshRetryDelayMs(response) {
  const retryAfter = response?.headers?.get("Retry-After");
  if (!retryAfter) return 60000;
  const retryAfterSeconds = Number.parseInt(retryAfter, 10);
  if (Number.isFinite(retryAfterSeconds)) {
    return Math.max(1000, retryAfterSeconds * 1000);
  }
  const retryAfterDate = Date.parse(retryAfter);
  if (Number.isFinite(retryAfterDate)) {
    return Math.max(1000, retryAfterDate - Date.now());
  }
  return 60000;
}

export function hasAuthSession() {
  return (
    localStorage.getItem(SESSION_KEY) === "true" ||
    !!localStorage.getItem("access") ||
    !!localStorage.getItem("refresh")
  );
}

/*
  LOGIN FUNCTION
  Sends username + password to your Django backend.
  If successful:
    - receives { access, refresh }
    - stores them in localStorage
*/
export async function login(username, password) {
  const response = await fetch(`${API_URL}/token/`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message =
      errorData?.detail ||
      errorData?.non_field_errors?.[0] ||
      "Invalid login";
    throw new Error(message);
  }

  const data = await response.json();

  replaceStoredAuthSession(data);

  return data; // return tokens in case you want to do something with them immediately
}

/*
  HELPER FUNCTIONS FOR GETTING TOKENS
*/
export function getAccessToken() {
  return localStorage.getItem("access");
}

export function getRefreshToken() {
  return localStorage.getItem("refresh");
}

export function getTokenExpiry(token) {
  if (!token) return 0;
  const parts = token.split(".");
  if (parts.length !== 3) return 0;
  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    return payload?.exp ? payload.exp * 1000 : 0;
  } catch {
    return 0;
  }
}

export function isTokenExpired(token, bufferSeconds = 30) {
  const expiry = getTokenExpiry(token);
  if (!expiry) return false;
  return Date.now() >= expiry - bufferSeconds * 1000;
}

export async function refreshAccessToken() {
  if (refreshPromise) {
    return refreshPromise;
  }

  if (refreshBlockedUntil > Date.now()) {
    const error = new Error("Refresh temporarily unavailable");
    error.status = 429;
    throw error;
  }

  refreshPromise = (async () => {
    const refresh = getRefreshToken();
    const body = refresh ? { refresh } : {};
    if (!refresh && localStorage.getItem(SESSION_KEY) !== "true") {
      const error = new Error("No refresh token");
      error.status = 401;
      throw error;
    }

    const response = await fetch(`${API_URL}/token/refresh/`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", ...csrfHeaders() },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const error = new Error("Refresh failed");
      error.status = response.status;
      if (response.status === 429) {
        refreshBlockedUntil = Date.now() + refreshRetryDelayMs(response);
      }
      throw error;
    }

    const data = await response.json();
    if (!data?.access) {
      const error = new Error("Refresh failed");
      error.status = 401;
      throw error;
    }

    markSessionActive(data);
    return data.access || "";
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

export function notifyAuthLogout() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("auth-logout"));
  }
}

/*
  LOGOUT FUNCTION
  Removes tokens from localStorage.
*/
export function logout({ remote = true } = {}) {
  const hadSession = clearStoredAuthSession();
  if (hadSession) {
    notifyAuthLogout();
  }

  if (remote && hadSession && !logoutRequestInFlight) {
    logoutRequestInFlight = true;
    fetch(`${API_URL}/logout/`, {
      method: "POST",
      credentials: "include",
      headers: csrfHeaders(),
    })
      .catch(() => {})
      .finally(() => {
        logoutRequestInFlight = false;
      });
  }
}

export async function requestPasswordReset(email) {
  const response = await fetch(`${API_URL}/auth/password-reset-request/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    throw new Error("Unable to send reset link");
  }

  return response.json();
}

export async function confirmPasswordReset(token, newPassword) {
  const response = await fetch(`${API_URL}/auth/password-reset-confirm/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, new_password: newPassword }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data?.detail || "Reset link is invalid or expired");
  }

  return response.json();
}

export async function confirmEmailVerification(token) {
  const response = await fetch(`${API_URL}/auth/email-verification/confirm/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data?.detail || "Verification link is invalid or expired");
  }

  return response.json();
}

export async function confirmAccountMergeRequest(token) {
  const response = await fetch(`${API_URL}/account-settings/merge-requests/confirm/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data?.detail || "Account-help link is invalid or expired");
  }

  return response.json();
}
